import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { generateCycleCountLines, postCycleCount } from "@/lib/inventory.functions";
import {
  getCycleCount, listCycleCountLines, listFacilityLocations, listTenantSkus,
  updateCycleCountLine, addCycleCountLine, cancelCycleCount, deleteCycleCount,
} from "@/lib/cycle-counts.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { HelpButton } from "@/components/kb/help-button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Loader2, ArrowLeft, PackageCheck, X, Trash, ListPlus, MoreHorizontal } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { StatusPill } from "@/components/ui/status-pill";

export const Route = createFileRoute("/_authenticated/app/cycle-counts/$id")({ component: CycleCountDetailPage });

function CycleCountDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { tenantId } = useScope();
  const generateLinesFn = useServerFn(generateCycleCountLines);
  const postCycleCountFn = useServerFn(postCycleCount);
  const getCycleCountFn = useServerFn(getCycleCount);
  const listLinesFn = useServerFn(listCycleCountLines);
  const listLocationsFn = useServerFn(listFacilityLocations);
  const listSkusFn = useServerFn(listTenantSkus);
  const updateLineFn = useServerFn(updateCycleCountLine);
  const addLineFn = useServerFn(addCycleCountLine);
  const cancelFn = useServerFn(cancelCycleCount);
  const deleteFn = useServerFn(deleteCycleCount);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedLocs, setSelectedLocs] = useState<Record<string, boolean>>({});
  const [generating, setGenerating] = useState(false);
  const [posting, setPosting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [mSku, setMSku] = useState("");
  const [mLoc, setMLoc] = useState("");
  const [mLot, setMLot] = useState("");
  const [mExpected, setMExpected] = useState("0");
  const [mAdding, setMAdding] = useState(false);
  const [blind, setBlind] = useState(true);

  const { data: cc } = useQuery({
    queryKey: ["cycle-count", id],
    queryFn: () => getCycleCountFn({ data: { id } }),
  });

  const { data: linesData } = useQuery({
    queryKey: ["cycle-count-lines", id],
    queryFn: () => listLinesFn({ data: { id } }),
  });
  const lines = linesData?.lines ?? [];
  const skus = linesData?.skus ?? [];
  const locsForLines = linesData?.locations ?? [];

  const { data: locations = [] } = useQuery({
    queryKey: ["cc-locations", cc?.facility_id],
    enabled: !!cc?.facility_id,
    queryFn: () => listLocationsFn({ data: { facilityId: cc!.facility_id } }),
  });

  const { data: allSkus = [] } = useQuery({
    queryKey: ["cc-all-skus", cc?.tenant_id],
    enabled: !!cc?.tenant_id && manualOpen,
    queryFn: () => listSkusFn({ data: { tenantId: cc!.tenant_id } }),
  });

  const skuMap = useMemo(() => Object.fromEntries(skus.map((s: any) => [s.id, s])), [skus]);
  const locMap = useMemo(() => Object.fromEntries(locsForLines.map((l: any) => [l.id, l])), [locsForLines]);

  const editable = cc?.status === "open" || cc?.status === "counting" || cc?.status === "review";
  const counted = lines.filter((l) => l.counted_qty !== null).length;
  const variances = lines.filter((l) => l.counted_qty !== null && (l.variance ?? 0) !== 0).length;
  const netDelta = lines.reduce((sum, l) => sum + (l.counted_qty !== null ? (l.variance ?? 0) : 0), 0);

  const generate = async () => {
    const ids = Object.entries(selectedLocs).filter(([, v]) => v).map(([k]) => k);
    if (ids.length === 0) return toast.error("Pick at least one location");
    setGenerating(true);
    try {
      const { count } = await runServerFn(
        () => generateLinesFn({ data: { cycleCountId: id, locationIds: ids } }),
        "Generate lines",
      );
      toast.success(`Added ${count} lines`);
      setPickerOpen(false); setSelectedLocs({});
      qc.invalidateQueries({ queryKey: ["cycle-count-lines", id] });
      qc.invalidateQueries({ queryKey: ["cycle-count", id] });
    } catch {
      // toast handled
    } finally {
      setGenerating(false);
    }
  };

  const updateLine = async (lineId: string, patch: Partial<{ counted_qty: number | null; notes: string | null }>) => {
    try {
      await runServerFn(
        () => updateLineFn({ data: {
          lineId,
          ...(patch.counted_qty !== undefined ? { countedQty: patch.counted_qty } : {}),
          ...(patch.notes !== undefined ? { notes: patch.notes } : {}),
        } }),
        "Update line",
      );
      qc.invalidateQueries({ queryKey: ["cycle-count-lines", id] });
    } catch { /* toast handled */ }
  };

  const post = async () => {
    if (!confirm(`Post ${variances} variance line(s)? This writes adjustments to the inventory ledger.`)) return;
    setPosting(true);
    try {
      const { result } = await runServerFn(
        () => postCycleCountFn({ data: { cycleCountId: id } }),
        "Post cycle count",
      );
      const r = result as { lines_posted?: number; net_qty_delta?: number } | null;
      toast.success(`Posted ${r?.lines_posted ?? 0} adjustments (net ${r?.net_qty_delta ?? 0})`);
      qc.invalidateQueries({ queryKey: ["cycle-count", id] });
      qc.invalidateQueries({ queryKey: ["cycle-counts", tenantId] });
    } catch {
      // toast handled
    } finally {
      setPosting(false);
    }
  };

  const cancelCount = async () => {
    if (!confirm("Cancel this cycle count? This cannot be undone.")) return;
    setCancelling(true);
    try {
      await runServerFn(() => cancelFn({ data: { id } }), "Cancel count");
      qc.invalidateQueries({ queryKey: ["cycle-count", id] });
      qc.invalidateQueries({ queryKey: ["cycle-counts", tenantId] });
    } catch { /* toast handled */ } finally { setCancelling(false); }
  };

  const destroy = async () => {
    if (!confirm("Delete this cycle count permanently? Lines will be removed.")) return;
    try {
      await runServerFn(() => deleteFn({ data: { id } }), "Delete cycle count");
      toast.success("Cycle count deleted");
      qc.invalidateQueries({ queryKey: ["cycle-counts", tenantId] });
      navigate({ to: "/app/cycle-counts" });
    } catch { /* toast handled */ }
  };

  const addManual = async (e: React.FormEvent) => {
    e.preventDefault();
    const exp = Number(mExpected);
    if (!mSku || !mLoc || !Number.isFinite(exp) || exp < 0) return toast.error("Pick SKU, location, and expected qty ≥ 0");
    setMAdding(true);
    try {
      await runServerFn(
        () => addLineFn({ data: {
          cycleCountId: id, skuId: mSku, locationId: mLoc,
          lot: mLot.trim() || null, expectedQty: exp,
        } }),
        "Add line",
      );
      toast.success("Line added");
      setManualOpen(false); setMSku(""); setMLoc(""); setMLot(""); setMExpected("0");
      qc.invalidateQueries({ queryKey: ["cycle-count-lines", id] });
    } catch { /* toast handled */ } finally { setMAdding(false); }
  };

  return (
    <>
      <PageHeader
        title={cc?.name ?? "Cycle count"}
        description={cc?.notes ?? undefined}
        actions={
          <div className="flex items-center gap-2">
            <HelpButton moduleKey="cycle_counts" />
            <Button variant="outline" onClick={() => navigate({ to: "/app/cycle-counts" })}>
              <ArrowLeft className="h-4 w-4 mr-2" />All counts
            </Button>
            {editable && (
              <>
                <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
                  <DialogTrigger asChild><Button variant="outline"><Plus className="h-4 w-4 mr-2" />Add locations</Button></DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Pick locations to count</DialogTitle></DialogHeader>
                    <div className="max-h-80 overflow-auto border rounded-md divide-y">
                      {locations.map((l) => (
                        <label key={l.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!selectedLocs[l.id]}
                            onChange={(e) => setSelectedLocs((prev) => ({ ...prev, [l.id]: e.target.checked }))}
                          />
                          <span className="font-mono text-sm">{l.code}</span>
                          {l.zone && <span className="text-xs text-muted-foreground">zone {l.zone}</span>}
                        </label>
                      ))}
                      {locations.length === 0 && <div className="p-4 text-sm text-muted-foreground">No active locations.</div>}
                    </div>
                    <DialogFooter>
                      <Button onClick={generate} disabled={generating}>
                        {generating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Generate lines
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                <Dialog open={manualOpen} onOpenChange={setManualOpen}>
                  <DialogTrigger asChild><Button variant="outline"><ListPlus className="h-4 w-4 mr-2" />Add line</Button></DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Add line manually</DialogTitle></DialogHeader>
                    <form onSubmit={addManual} className="space-y-4">
                      <div className="space-y-2">
                        <Label>SKU</Label>
                        <Select value={mSku} onValueChange={setMSku}>
                          <SelectTrigger><SelectValue placeholder="Select SKU" /></SelectTrigger>
                          <SelectContent>{allSkus.map((s) => <SelectItem key={s.id} value={s.id}>{s.sku_code} — {s.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Location</Label>
                        <Select value={mLoc} onValueChange={setMLoc}>
                          <SelectTrigger><SelectValue placeholder="Select location" /></SelectTrigger>
                          <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.code}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2"><Label>Lot (optional)</Label><Input value={mLot} onChange={(e) => setMLot(e.target.value)} /></div>
                        <div className="space-y-2"><Label>Expected qty</Label><Input type="number" min="0" value={mExpected} onChange={(e) => setMExpected(e.target.value)} /></div>
                      </div>
                      <DialogFooter><Button type="submit" disabled={mAdding}>{mAdding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Add line</Button></DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
                <Button onClick={post} disabled={posting || lines.length === 0}>
                  {posting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  <PackageCheck className="h-4 w-4 mr-2" />Post variances
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" aria-label="More"><MoreHorizontal className="h-4 w-4" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={cancelCount} disabled={cancelling}>
                      <X className="h-4 w-4 mr-2" />Cancel count
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={destroy} className="text-destructive focus:text-destructive">
                      <Trash className="h-4 w-4 mr-2" />Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        }
      />
      <div className="p-4 sm:p-6 space-y-4">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <StatusPill tone="info">Status: {cc?.status ?? "—"}</StatusPill>
          <StatusPill>Lines: {lines.length}</StatusPill>
          <StatusPill tone={counted === lines.length && lines.length > 0 ? "success" : "info"}>
            Counted: {counted}/{lines.length}
          </StatusPill>
          {!blind && (
            <>
              <StatusPill tone={variances > 0 ? "warning" : "neutral"}>Variances: {variances}</StatusPill>
              <StatusPill>Net qty Δ: {netDelta}</StatusPill>
            </>
          )}
          <label className="ml-auto flex items-center gap-2 text-xs text-muted-foreground select-none">
            <span>Blind count</span>
            <Switch checked={blind} onCheckedChange={setBlind} aria-label="Hide expected and variance during entry" />
          </label>
        </div>
        <div className="border rounded-lg bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Location</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Lot</TableHead>
                {!blind && <TableHead className="text-right">Expected</TableHead>}
                <TableHead className="text-right w-32">Counted</TableHead>
                {!blind && <TableHead className="text-right">Variance</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.length === 0 ? (
                <TableRow><TableCell colSpan={blind ? 4 : 6} className="text-center py-10">
                  <div className="text-muted-foreground mb-3">No lines yet. Add lines to begin counting.</div>
                  {editable && (
                    <div className="flex items-center justify-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
                        <Plus className="h-4 w-4 mr-2" />Add from locations
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setManualOpen(true)}>
                        <ListPlus className="h-4 w-4 mr-2" />Add line manually
                      </Button>
                    </div>
                  )}
                </TableCell></TableRow>
              ) : lines.map((l) => {
                const sku = l.sku_id ? skuMap[l.sku_id] : null;
                const loc = locMap[l.location_id];
                const variance = l.counted_qty !== null ? (l.variance ?? 0) : null;
                return (
                  <TableRow key={l.id}>
                    <TableCell className="font-mono text-sm">{loc?.code ?? "—"}</TableCell>
                    <TableCell>{sku ? <><span className="font-mono text-xs">{sku.sku_code}</span> <span className="text-muted-foreground text-xs ml-1">{sku.name}</span></> : <span className="text-muted-foreground text-xs">empty</span>}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">{l.lot ?? "—"}</TableCell>
                    {!blind && <TableCell className="text-right tabular-nums">{l.expected_qty}</TableCell>}
                    <TableCell className="text-right">
                      {editable ? (
                        <Input
                          type="number"
                          className="h-10 text-right tabular-nums text-base font-medium"
                          defaultValue={l.counted_qty ?? ""}
                          onBlur={(e) => {
                            const v = e.target.value;
                            const next = v === "" ? null : Number(v);
                            if (next === l.counted_qty) return;
                            updateLine(l.id, { counted_qty: next });
                          }}
                        />
                      ) : (
                        <span className="tabular-nums">{l.counted_qty ?? "—"}</span>
                      )}
                    </TableCell>
                    {!blind && (
                      <TableCell className={`text-right tabular-nums ${variance === null ? "text-muted-foreground" : variance === 0 ? "" : variance > 0 ? "text-success" : "text-destructive"}`}>
                        {variance === null ? "—" : variance > 0 ? `+${variance}` : variance}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <p className="text-xs text-muted-foreground">
          Posting writes <span className="font-mono">adjust</span> entries to the inventory ledger for each non-zero variance.
          <Link to="/app/inventory" className="ml-1 underline">View inventory</Link>
        </p>
      </div>
    </>
  );
}